// Packages
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';

// Components
import { KiteSelect } from '@kite/react-kite';
// Utils
import { TermYears } from 'types/TcoPrices';
import { useQueryData } from 'hooks';
import { useTcoDashboardExports } from 'hooks/useTcoDashboardExports';
import { Toast } from '@kite/react-kite-plus';
import ContextMenu from '../ContextMenu';
import { TToast } from 'types';
import './RunRateChart.scss';
import LegalDisclaimers from '../LegalDisclaimers';

export interface ILineGraph {
  selectedTerm: number;
  spectrumOneTimeCost: number;
  spectrumOngoingMonthlyCost: number;
  diyOneTimeCost: number;
  diyOngoingMonthlyCost: number;
  spectrumPromotions: number;
}

const RunRateChart = ({
  selectedTerm,
  spectrumOneTimeCost,
  spectrumOngoingMonthlyCost,
  diyOneTimeCost,
  diyOngoingMonthlyCost,
  spectrumPromotions = 0,
}: ILineGraph) => {
  // =============================================
  // Helpers (Memo, CB, vars)
  // =============================================

  // =============================================
  // Return
  // =============================================

  const { tcoDeviceSelections } = useQueryData();

  const termInMonths = selectedTerm * 12;

  const [currentTerm, setCurrentTerm] = useState<string>(`${selectedTerm}`);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const [toast, setToast] = useState<TToast>(null);

  const labels = useMemo(() => {
    return Array.from(
      { length: Number(currentTerm) * 12 + 1 },
      (_, i) => ` ${i + 1}`
    );
  }, [currentTerm]);

  const { downloadChartAsImage, copyChartToClipBoard, handleChartPdf } =
    useTcoDashboardExports(mainRef);

  const { totalLicensingCost } = useMemo(() => {
    return tcoDeviceSelections.reduce(
      (totals, selection) => {
        const { licenseCost, quantity } = selection;

        totals.totalLicensingCost += licenseCost * quantity;
        return totals;
      },
      {
        totalLicensingCost: 0,
      }
    );
  }, [tcoDeviceSelections]);

  const generateCumulativeData = useCallback(
    (
      oneTimeCost: number,
      onGoingMonthlyCost: number,
      liscensingCost = 0,
      promotions = 0
    ) => {
      let cumulativeData: string[] = [];
      let cumulativeAmount = oneTimeCost - promotions;
      const currentTermInMonths = Number(currentTerm) * 12;

      for (let index = 1; index <= currentTermInMonths + 1; index++) {
        if (index >= 2) cumulativeAmount += onGoingMonthlyCost;

        if (index === termInMonths + 1) {
          cumulativeAmount += liscensingCost;
        }
        cumulativeData.push(Number(cumulativeAmount).toFixed(2));
      }
      return cumulativeData;
    },
    [currentTerm, termInMonths]
  );

  const data = useMemo(
    () => ({
      labels: labels,
      datasets: [
        {
          label: 'Cumulative DIY',
          data: generateCumulativeData(
            diyOneTimeCost,
            diyOngoingMonthlyCost,
            totalLicensingCost
          ),
          fill: false,
          backgroundColor: '#002139',
          borderColor: '#002139',
          tension: 0.1,
          pointStyle: 'circle',
          pointRadius: 0,
          pointHitRadius: 20,
        },
        {
          label: 'Cumulative Spectrum',
          data: generateCumulativeData(
            spectrumOneTimeCost,
            spectrumOngoingMonthlyCost,
            0,
            spectrumPromotions
          ),
          fill: false,
          backgroundColor: '#0271EB',
          borderColor: '#0271EB',
          tension: 0.1,
          pointStyle: 'circle',
          pointRadius: 0,
          pointHitRadius: 20,
        },
      ],
    }),
    [
      labels,
      generateCumulativeData,
      diyOneTimeCost,
      diyOngoingMonthlyCost,
      totalLicensingCost,
      spectrumOneTimeCost,
      spectrumOngoingMonthlyCost,
      spectrumPromotions,
    ]
  );

  const options = useMemo(
    () => ({
      responsiveAnimationDuration: false,
      responsive: true,
      maintainAspectRatio: false,
      backgroundColor: 'white',
      devicePixelRatio: 2,
      animation: {
        duration: 500,
      },
      title: {
        display: true,
        text: `${Number(currentTerm) * 12}-Month Cumulative Project Spend`,
        fontSize: 20,
        fontStyle: 400,
        fontColor: '#000',
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointstyle: true,
          pointStyle: 'line',
          fontSize: 12,
          fontColor: '#000',
        },
      },
      tooltips: {
        enabled: true,
        mode: 'index',
        intersect: true,
        position: 'nearest',
        titleFontSize: 14,
        bodyFontSize: 12,
        callbacks: {
          title: function (tooltipItems: any[]) {
            return `Month ${tooltipItems[0]?.xLabel}`;
          },
          label: function (tooltipItem: any, data: any) {
            let label =
              data.datasets?.[tooltipItem.datasetIndex || 0]?.label || '';
            label = label.replace('Cumulative', '');

            return ` ${label} : $${tooltipItem.yLabel.toLocaleString()}`;
          },
        },
        usePointStyle: true,
      },
      hover: {
        mode: 'index',
        axis: 'x',
        intersect: true,
      },

      scales: {
        yAxes: [
          {
            ticks: {
              fontSize: 12,
              beginAtZero: true,
              callback: function (value: number) {
                return '$' + value.toLocaleString();
              },
              fontStyle: 400,

              fontColor: '#000',
            },
            gridLines: {
              drawBorder: true,
              display: true,
            },
          },
        ],

        xAxes: [
          {
            ticks: {
              fontSize: 12,
              fontStyle: 400,

              fontColor: '#000',
              min: 1,
              stepSize: 1,
              callback: function (value: number, index: number) {
                return index % 3 === 0 ? value : '';
              },
              autoSkip: true,
              maxRotation: 2,
            },
            gridLines: {
              display: false,
              drawBorder: true,
            },
            offset: true,
          },
        ],
      },
    }),
    [currentTerm]
  );

  const displayTerm = useCallback((term: string) => {
    return `${term} ${term === '1' ? 'Year' : 'Years'}`;
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      const formattedValue = value.replace(/\D/g, '');
      setCurrentTerm(formattedValue);
    },
    []
  );

  const displayOptions = useMemo(() => {
    return Object.keys(TermYears).map((value) => (
      <option key={`Term Option ${value}`} value={displayTerm(value)}>
        {displayTerm(value)}
      </option>
    ));
  }, [displayTerm]);

  const checkEmptyData =
    spectrumOneTimeCost === 0 &&
    spectrumOngoingMonthlyCost === 0 &&
    diyOneTimeCost === 0 &&
    diyOngoingMonthlyCost === 0;

  return (
    <>
      {' '}
      <Toast newToast={toast} uniqueKey="text" autoClose={3000} />
      <div className="kite-card tco-line-graph" ref={mainRef} id="line-graph">
        <div className="top-space" />
        <div className="tco-line-graph__header">
          <h2>Run Rate Chart</h2>
          {!checkEmptyData && (
            <div className="tco-line-graph__term-controls">
              <span style={{ marginRight: '8px' }}>Term</span>

              <KiteSelect
                id="dashboard-dropdown-term"
                name="devices"
                value={displayTerm(currentTerm)}
                onChange={handleChange}
                className="dashboard-dropdown"
              >
                {displayOptions}
              </KiteSelect>

              <div id="context-section">
                <ContextMenu>
                  <li
                    onClick={() => {
                      copyChartToClipBoard(setToast, 'line-graph');
                    }}
                  >
                    Copy Chart to Clipboard
                  </li>
                  <li
                    onClick={() => {
                      downloadChartAsImage('run-rate-chart', 'line-graph');
                    }}
                  >
                    Download as Image
                  </li>
                  <li
                    onClick={() => {
                      handleChartPdf('Cumulative Chart');
                    }}
                  >
                    Download as PDF
                  </li>
                </ContextMenu>
              </div>
            </div>
          )}
        </div>

        {checkEmptyData ? (
          <p>No data to display.</p>
        ) : (
          <div className="tco-line-graph__container chart-container">
            <Line
              key={`${labels.length}-${currentTerm}`}
              data={data}
              options={options}
            />
          </div>
        )}

        <LegalDisclaimers />
      </div>
    </>
  );
};

export default RunRateChart;
